import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { DocCallout, DocCodeBlock, DocSection } from '../docs-components';

const Authentication: FC = () => (
  <Box>
    <Typography variant="h4" fontWeight={700} gutterBottom>
      Authentication
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      All API requests must be authenticated. The Thermia API uses API keys passed as a Bearer token in the HTTP Authorization header.
    </Typography>

    <DocSection title="Creating an API Key">
      <Typography variant="body1" gutterBottom>
        API keys are created per device in the <strong>API Keys</strong> section of this portal. Each key is scoped to a single device and a set of
        permissions. You must have physical access to the device to verify ownership during key creation.
      </Typography>
    </DocSection>

    <DocSection title="Using Your API Key">
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        Include the API key as a Bearer token in every request:
      </Typography>
      <DocCodeBlock language="http">
        {`GET /v1/devices/abc123/status HTTP/1.1
Host: api.thermiaonline.com
Authorization: Bearer YOUR_API_KEY`}
      </DocCodeBlock>

      <Typography variant="body1" sx={{ mt: 2, mb: 1.5 }}>
        Example with <strong>curl</strong>:
      </Typography>
      <DocCodeBlock language="bash">
        {`curl https://api.thermiaonline.com/v1/devices/abc123/status \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
      </DocCodeBlock>

      <Typography variant="body1" sx={{ mt: 2, mb: 1.5 }}>
        Example with <strong>JavaScript / fetch</strong>:
      </Typography>
      <DocCodeBlock language="javascript">
        {`const response = await fetch(
  'https://api.thermiaonline.com/v1/devices/abc123/status',
  {
    headers: {
      Authorization: 'Bearer YOUR_API_KEY',
    },
  }
);
const data = await response.json();`}
      </DocCodeBlock>
    </DocSection>

    <DocSection title="Key Scopes &amp; Permissions">
      <Typography variant="body1" gutterBottom>
        When creating a key you choose the permission level:
      </Typography>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            padding: '8px 12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            typography: 'body2'
          },
          '& th': { fontWeight: 700, bgcolor: 'action.hover' }
        }}>
        <thead>
          <tr>
            <th>Scope</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['read', 'Read-only access to device status and sensor data.'],
            ['read_history', 'Read access including historical time-series data.'],
            ['control', 'Read access plus the ability to send control commands.']
          ].map(([scope, desc]) => (
            <tr key={scope}>
              <td>
                <code>{scope}</code>
              </td>
              <td>{desc}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </DocSection>

    <DocSection title="Key Expiry &amp; Rotation">
      <Typography variant="body1">
        API keys have an expiry date set at creation time. When a key expires all requests using it will return <code>401 Unauthorized</code>. You can
        create a new key to replace an expiring one at any time from the <strong>API Keys</strong> page.
      </Typography>
    </DocSection>

    <DocCallout type="warning">
      Treat your API key like a password. Never commit it to source control or expose it in client-side code. Use environment variables or a secrets
      manager to store it safely.
    </DocCallout>
  </Box>
);

export default Authentication;
